import { useSyncExternalStore } from 'react';

/**
 * Which design this person works in.
 *
 * It used to be a parameter on the address, which meant every link had to carry it and any link
 * that forgot dropped the reader back into the classic app mid-journey. That is not a bug you fix
 * once: it is a bug you fix per link, forever, and we fixed six of them before writing this.
 *
 * So it is a preference now — a fact about the person, not about the page. Links carry nothing;
 * pages ask. `?design=` still overrides it for one address, because sometimes you want to send a
 * colleague the other design without changing what they use.
 *
 * Stored in the browser until there is a field on the user to keep it in. Losing it on a new
 * machine costs one click.
 */
const KEY = 'datox.design';

const read = () => {
  try {
    return window.localStorage.getItem(KEY) === 'v2' ? 'v2' : 'classic';
  } catch {
    /* Private mode, blocked storage: the classic app is the safe answer. */
    return 'classic';
  }
};

/* One store, so the switch in the top bar and the page under it never disagree — the pages that
   hold both designs at one address re-render off this, not off a route change. */
const listeners = new Set();
let current = read();

const subscribe = listener => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const designPreference = () => current;

export const setDesignPreference = design => {
  const next = design === 'v2' ? 'v2' : 'classic';
  if (next === current) return;

  current = next;
  try {
    window.localStorage.setItem(KEY, next);
  } catch {
    /* Unstored but honoured for this session, which is the half that matters. */
  }
  listeners.forEach(listener => listener());
};

export const setDesignDefault = design => {
  try {
    if (window.localStorage.getItem(KEY) !== null) return;
  } catch {
    return;
  }
  current = design;
  listeners.forEach(listener => listener());
};

/* Another tab is the same person: switching there switches here. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key !== KEY) return;
    current = read();
    listeners.forEach(listener => listener());
  });
}

/* ── Where you are ───────────────────────────────────────────────────────────
   Some pages hold both designs at one address; others gave the redesign a route of its own. On
   those the route is the answer and the preference has nothing to say: following a direct link to
   the classic report puts you on the classic report, whatever you usually use. */
const V2_ROUTES = [
  '/report-v2',
  '/extraction-v2',
  '/transformation-v2',
  '/aifs-v2',
  '/jurisdictions-v2',
  '/output'
];

/* What is left of it. Five of these addresses now hold both designs and decide for themselves, so
   they say nothing about which design you are in. The report is the one that still pins: its
   classic page lives at a different address, with the regulator and the member state in the path,
   and a link to it is a link to it. */
const CLASSIC_ROUTES = ['/report'];

const matches = (pathname, route) => {
  if (route.includes(':')) {
    const shape = new RegExp(`^${route.replace(/:[^/]+/g, '[^/]+')}(/|$)`);
    return shape.test(pathname);
  }
  return pathname === route || pathname.startsWith(`${route}/`);
};

const onRoute = (pathname, routes) => routes.some(route => matches(pathname, route));

export const isV2Surface = (
  pathname = window.location.pathname,
  search = window.location.search
) => {
  if (onRoute(pathname, V2_ROUTES)) return true;
  if (onRoute(pathname, CLASSIC_ROUTES)) return false;

  const override = new URLSearchParams(search).get('design');
  // Anything but `classic` is a redesign variant — Source Files names its own (`v3a`, `v3b`).
  if (override) return override !== 'classic';

  return current === 'v2';
};

/** Reads the same answer, and re-renders when it changes. */
export const useIsV2Surface = (pathname, search) => {
  useSyncExternalStore(subscribe, designPreference, designPreference);
  return isV2Surface(pathname, search);
};

/** The setting on its own, for the route that holds both designs and asks nothing of the address. */
export const useDesignPreference = () =>
  useSyncExternalStore(subscribe, designPreference, designPreference);

/** A route only the redesign has means you are in it — keep the setting in step with the page. */
export const syncDesignToRoute = pathname => {
  if (onRoute(pathname, V2_ROUTES)) setDesignPreference('v2');
};

/**
 * Which of two addresses to open.
 *
 * Route pairs still need choosing between — that is not a flag being carried, it is two pages that
 * exist. Shared components name both and this picks, so the classic periods page keeps going
 * exactly where it always went.
 */
export const designPath = (v2Path, legacyPath, pathname, search) =>
  isV2Surface(pathname, search) ? v2Path : legacyPath;

/* ── The same page, in the other design ──────────────────────────────────────
   One switch needs to know, for wherever you are standing, where its counterpart is. Every page
   used to answer that for itself and six of them answered differently.

   For the pages that hold both designs at one address there is nowhere to go: the preference has
   changed and they re-render. All this has to do is drop a `?design=` override, or the address
   would go on outranking the preference the reader has just set.

   Returning null means the page has no counterpart. The switch draws nothing rather than offering a
   door into a room that was never built. */
const SAME_ADDRESS = [
  /^\/project$/,
  /^\/project\/quaters\/[^/]+$/,
  /^\/project\/consolidated\/[^/]+$/,
  /\/upload-files$/,
  /\/add-aifs$/,
  /\/jurisdictions$/,
  /^\/project\/[^/]+\/jurisdictions$/,
  /^\/data-extraction\//,
  /^\/output-tables\//
];

const PAIRS = [
  {
    v2: /^\/report-v2\/([^/]+)\/([^/]+)/,
    /* `/report/{esma|fca|pf}/{memberState}/{workflow}/{period}` — the regulator and the jurisdiction
       live in the path, and the redesigned page carries the second as `?ms=` and works the first
       out for itself. Going back needs both, so the report page hands the switch its own way home
       (`designSwitch` on the chrome context) and this only has to get you there.

       The last pair. Every other stage collapsed onto one address that serves both designs, and
       has nowhere to walk to: the page under the switch simply redraws. */
    classic: /^\/report\/[^/]+\/([^/]+)\/([^/]+)\/([^/]+)/,
    toV2: (ms, wf, pid) => `/report-v2/${wf}/${pid}?ms=${ms}`,
    toClassic: null
  },
  /* Three redesigned stages answer at TWO addresses: their own route, which the stepper links, and
     the classic address that serves both designs and switches in place. Only the second knew it had
     a counterpart, so standing on the first the switch drew nothing and read as "this page has no
     classic version" — reported for transformation, and true of the other two the same way. These
     say where the classic form is; the way back needs no rule, because the classic address is in
     SAME_ADDRESS and simply redraws. */
  {
    v2: /^\/transformation-v2\/([^/]+)\/([^/]+)/,
    classic: null,
    toV2: null,
    toClassic: (wf, pid) => `/data-extraction/${wf}/${pid}?tab=transformation`
  },
  {
    v2: /^\/extraction-v2\/([^/]+)\/([^/]+)/,
    classic: null,
    toV2: null,
    toClassic: (wf, pid) => `/data-extraction/${wf}/${pid}?tab=extraction`
  },
  {
    v2: /^\/output\/([^/]+)\/([^/]+)/,
    classic: null,
    toV2: null,
    toClassic: (wf, pid) => `/output-tables/${wf}/${pid}`
  }
];

/* Which file, which sheet, which category — the three the extraction and setup pages key their
   state off; and the jurisdiction a grouped report is scoped to, without which the report page
   reads the workflow id as something else entirely. Changing the design should not also change
   what you were looking at. */
const CONTEXT_KEYS = ['fileId', 'sheetId', 'category', 'jurisdictionId'];

const keepContext = (path, params) => {
  const [base, query = ''] = String(path).split('?');
  const next = new URLSearchParams(query);
  CONTEXT_KEYS.forEach(key => {
    const value = params.get(key);
    if (value != null && !next.has(key)) next.set(key, value);
  });
  const merged = next.toString();
  return merged ? `${base}?${merged}` : base;
};

export const designCounterpart = (
  pathname = window.location.pathname,
  search = window.location.search
) => {
  const params = new URLSearchParams(search);

  if (SAME_ADDRESS.some(shape => shape.test(pathname))) {
    const next = new URLSearchParams(params);
    next.delete('design');
    /* Page numbers do not survive a change of layout: the card list and the table paginate the same
       query, and landing on page 14 of a design you have just switched into is disorienting. */
    next.delete('page');
    const query = next.toString();
    return { to: query ? `${pathname}?${query}` : pathname, sameAddress: true };
  }

  for (const pair of PAIRS) {
    const asV2 = pathname.match(pair.v2);
    if (asV2) {
      if (!pair.toClassic) return null;
      return { to: keepContext(pair.toClassic(asV2[1], asV2[2]), params) };
    }

    // One-way pairs: the classic address is in SAME_ADDRESS and answers for itself.
    if (!pair.classic) continue;

    const asClassic = pathname.match(pair.classic);
    if (!asClassic) continue;
    if (pair.tab && (params.get('tab') || 'extraction') !== pair.tab) continue;

    return { to: keepContext(pair.toV2(...asClassic.slice(1)), params) };
  }

  return null;
};
